import React, { useEffect, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import {
  Siren,
  Clock,
  Truck,
  Navigation,
  AlertOctagon,
  Crosshair,
  Search,
  Filter,
  ChevronRight,
  X,
  Map as MapIcon,
  Layers,
  Activity,
  AlertTriangle,
  User,
  FileText,
  CheckCircle,
  ShieldAlert,
} from "lucide-react";
import { IoColorFillOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { da } from "date-fns/locale";
import { createRecord, fetchBlockRecords } from "@/features/records/records";
import { notify } from "../Notify/Notify";

const hotspotsData = [
  {
    id: "INC-001",
    coords: "26.9124° N, 75.7873° E",
    locationName: "Outskirts Sector 14 (Near Highway)",
    intensity: "High",
    frequency: 45,
    riskScore: 92,
    avgStopDuration: "35m",
    peakTime: "02:00 AM - 04:00 AM",
    flags: ["No RFID", "> 20 Mins", "Cluster"],
    mapPosition: { top: "30%", left: "40%" },
    isLogged: false,
    isDispatched: false,
    riskProfile: [
      { subject: "Duration", A: 120, fullMark: 150 },
      { subject: "Freq", A: 98, fullMark: 150 },
      { subject: "RFID", A: 130, fullMark: 150 },
      { subject: "Night", A: 140, fullMark: 150 },
      { subject: "Route Dev", A: 85, fullMark: 150 },
    ],
    tankersInvolved: [
      {
        id: "TN-04-1234",
        driver: "Ramesh K.",
        model: "Ashok Leyland 4x4",
        history: "3 Prev Violations",
      },
      {
        id: "RJ-14-5566",
        driver: "Suresh P.",
        model: "Tata Signa",
        history: "Clean Record",
      },
      {
        id: "RJ-14-9988",
        driver: "Unknown",
        model: "BharatBenz",
        history: "Suspect List",
      },
    ],
  },
  {
    id: "INC-002",
    coords: "26.8900° N, 75.8000° E",
    locationName: "Industrial Area Phase 2",
    intensity: "Medium",
    frequency: 18,
    riskScore: 65,
    avgStopDuration: "22m",
    peakTime: "11:00 PM - 01:00 AM",
    flags: ["> 20 Mins"],
    mapPosition: { top: "65%", left: "60%" },
    isLogged: true,
    isDispatched: false,
    riskProfile: [
      { subject: "Duration", A: 80, fullMark: 150 },
      { subject: "Freq", A: 40, fullMark: 150 },
      { subject: "RFID", A: 20, fullMark: 150 },
      { subject: "Night", A: 90, fullMark: 150 },
      { subject: "Route Dev", A: 110, fullMark: 150 },
    ],
    tankersInvolved: [
      {
        id: "TN-02-3344",
        driver: "Vikram S.",
        model: "Eicher Pro",
        history: "1 Prev Violation",
      },
    ],
  },
  {
    id: "INC-003",
    coords: "26.9500° N, 75.7500° E",
    locationName: "Remote Village Road",
    intensity: "Medium",
    frequency: 12,
    riskScore: 58,
    avgStopDuration: "28m",
    peakTime: "05:00 AM",
    flags: ["No RFID", "Unregistered"],
    mapPosition: { top: "20%", left: "70%" },
    isLogged: false,
    isDispatched: true,
    riskProfile: [
      { subject: "Duration", A: 60, fullMark: 150 },
      { subject: "Freq", A: 30, fullMark: 150 },
      { subject: "RFID", A: 120, fullMark: 150 },
      { subject: "Night", A: 50, fullMark: 150 },
      { subject: "Route Dev", A: 60, fullMark: 150 },
    ],
    tankersInvolved: [
      {
        id: "RJ-14-7788",
        driver: "Mohit D.",
        model: "Tata Prima",
        history: "Clean Record",
      },
    ],
  },
];

const timePatternData = [
  { time: "22:00", stops: 5 },
  { time: "00:00", stops: 12 },
  { time: "02:00", stops: 35 }, // Peak
  { time: "04:00", stops: 20 },
  { time: "06:00", stops: 2 },
];

const IntensityBadge = ({ level }) => {
  const colors = {
    High: "bg-red-600 text-white shadow-md shadow-red-500/40 border border-red-500",
    Medium:
      "bg-orange-500 text-white shadow-md shadow-orange-500/40 border border-orange-500",
    Low: "bg-yellow-500 text-white border border-yellow-500",
  };
  return (
    <span
      className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
        colors[level] || colors.Low
      }`}
    >
      {level} Alert
    </span>
  );
};

export default function IllegalHeatmapDashboard() {
  const themeMode = useSelector
    ? useSelector((state) => state.theme.mode)
    : false;
  const bgColorClass = themeMode ? "bg-slate-900" : "bg-slate-50";
  const textColorClass = themeMode ? "text-slate-100" : "text-slate-900";

  // --- State ---
  const [data, setData] = useState(hotspotsData);
  const [selectedSpot, setSelectedSpot] = useState(data[0]);
  const [selectedTanker, setSelectedTanker] = useState(null);
  const [loadingStates, setLoadingStates] = useState({
    log: {},
    dispatch: {}
  });

  const { blockRecords, loading } = useSelector((state) => state.record);

  const [searchQuery, setSearchQuery] = useState("");
  const [logicFilters, setLogicFilters] = useState({
    longStop: false,
    noRfid: false,
    unregistered: false,
  });

  const dispatch = useDispatch();

  /* define block ID */
  const logBlockId = "545a3024-fe62-4ac9-9b23-0be93dd552b4";
  const ticketBlockId = "9e06f717-6492-49b4-b196-30ed2780e9ae";

  /* define all data */
  const fieldId = {
    vehicleNumber: "18dd860a-8b36-478d-b133-2dba8fd60d2d",
    driverName: "43a25516-ffc7-48b2-9796-5303d89d84b4",
    location: "dde91449-4a16-4ef0-b2f8-81ff3745e7c5",
    caseId: "a0d41e0f-b86f-45ec-a63e-b44faf1d3a82",
    lat: "c8ade7df-73b0-4c50-8bf4-677fca7e0a71",
    lng: "d366c806-2033-4167-90c4-d86697c211ef",
  };

  const dataWithValue = [
    {
      "18dd860a-8b36-478d-b133-2dba8fd60d2d": "TN-04-1234",
      "43a25516-ffc7-48b2-9796-5303d89d84b4": "Ramesh Kumar",
      "dde91449-4a16-4ef0-b2f8-81ff3745e7c5": "Sector 14, Jaipur",
      "a0d41e0f-b86f-45ec-a63e-b44faf1d3a82": "CASE001",
      "c8ade7df-73b0-4c50-8bf4-677fca7e0a71": "In Transit",
      "d366c806-2033-4167-90c4-d86697c211ef": "Team Alpha",
    },
    {
      "18dd860a-8b36-478d-b133-2dba8fd60d2d": "RJ-14-5566",
      "43a25516-ffc7-48b2-9796-5303d89d84b4": "Suresh Singh",
      "dde91449-4a16-4ef0-b2f8-81ff3745e7c5": "Near Industrial Area",
      "a0d41e0f-b86f-45ec-a63e-b44faf1d3a82": "CASE002",
      "c8ade7df-73b0-4c50-8bf4-677fca7e0a71": "Stopped",
      "d366c806-2033-4167-90c4-d86697c211ef": "Team Beta",
    },
    {
      "18dd860a-8b36-478d-b133-2dba8fd60d2d": "RJ-14-9988",
      "43a25516-ffc7-48b2-9796-5303d89d84b4": "Amit Sharma",
      "dde91449-4a16-4ef0-b2f8-81ff3745e7c5": "Outskirts Sector 14",
      "a0d41e0f-b86f-45ec-a63e-b44faf1d3a82": "CASE003",
      "c8ade7df-73b0-4c50-8bf4-677fca7e0a71": "In Transit",
      "d366c806-2033-4167-90c4-d86697c211ef": "Team Gamma",
    },
  ];

  const [fillData, setFillData] = useState([
    { time: "00:00", value: 20 },
    { time: "04:00", value: 35 },
    { time: "08:00", value: 25 },
  ]);

  const handleLogCase = async (e, id) => {
    e.stopPropagation();
    
    // Set loading state for this specific hotspot
    setLoadingStates(prev => ({
      ...prev,
      log: { ...prev.log, [id]: true }
    }));

    let allSuccess = true;
    
    try {
      // Log all records
      for (let i = 0; i < dataWithValue.length; i++) {
        const preparedData = dataWithValue[i];
        const response = await dispatch(
          createRecord({ blockId: logBlockId, recordData: preparedData })
        ).unwrap();
        
        if (!(response?.status === "success" || response?.status === 200 || response?.success === true)) {
          allSuccess = false;
        }
      }

      if (allSuccess) {
        // Fetch updated records
        dispatch(fetchBlockRecords({ blockId: logBlockId }));
        
        // Update UI - mark both isLogged and isDispatched as false initially
        const updated = data.map((item) =>
          item.id === id ? { 
            ...item, 
            isLogged: true, 
          } : item
        );
        setData(updated);
        setSelectedSpot(updated.find((i) => i.id === id));
        
        notify({
          type: "success",
          message: "Log Created",
        });
      } else {
        notify({
          type: "error",
          message: "Something went wrong",
        });
      }
    } catch (error) {
      console.error("Log case error:", error);
      notify({
        type: "error",
        message: "Failed to create log",
      });
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({
        ...prev,
        log: { ...prev.log, [id]: false }
      }));
    }
  };

  useEffect(() => {
    dispatch(fetchBlockRecords({ blockId: logBlockId }));
  }, [dispatch, logBlockId]);

  const handleDispatch = async (e, id) => {
    e.stopPropagation();
    
    // Guard clause: Check if blockRecords is empty
    if (!blockRecords || blockRecords.length === 0) {
      notify({
        type: "error",
        message: "No records available to dispatch",
      });
      return;
    }
    
    // Set loading state for this specific hotspot
    setLoadingStates(prev => ({
      ...prev,
      dispatch: { ...prev.dispatch, [id]: true }
    }));

    let allSuccess = true;
    
    try {
      // Prepare dispatch data
      const preparedData = [];
      const maxRecords = Math.min(3, blockRecords.length); 
      for (let i = 0; i < maxRecords; i++) {
        preparedData.push({
          "7b34c09d-672e-49eb-9af0-734878bb8696": {
            id: blockRecords[i]?.id,
            value: blockRecords[i]?.id,
          },
          "9d9c1a98-aa62-49d9-a43a-8ae3153de9a8": {
            id: "Un Resolved",
            value: "Un Resolved",
          },
        });
      }

      // Dispatch all records
      for (let i = 0; i < preparedData.length; i++) {
        const response = await dispatch(
          createRecord({ blockId: ticketBlockId, recordData: preparedData[i] })
        ).unwrap();
        
        if (!(response?.status === "success" || response?.status === 200 || response?.success === true)) {
          allSuccess = false;
        }
      }

      if (allSuccess) {
        // Update UI
        const updated = data.map((item) =>
          item.id === id ? { ...item, isDispatched: true } : item
        );
        setData(updated);
        setSelectedSpot(updated.find((i) => i.id === id));
        
        notify({
          type: "success",
          message: "Dispatched Successfully",
        });
      } else {
        notify({
          type: "error",
          message: "Something went wrong",
        });
      }
    } catch (error) {
      console.error("Dispatch error:", error);
      notify({
        type: "error",
        message: "Failed to dispatch",
      });
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({
        ...prev,
        dispatch: { ...prev.dispatch, [id]: false }
      }));
    }
  };

  const toggleLogicFilter = (key) =>
    setLogicFilters((prev) => ({ ...prev, [key]: !prev[key] }));

  const filteredHotspots = data.filter((spot) => {
    const matchesSearch = spot.locationName
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    let matchesLogic = true;
    if (logicFilters.longStop && !spot.flags.includes("> 20 Mins"))
      matchesLogic = false;
    if (logicFilters.noRfid && !spot.flags.includes("No RFID"))
      matchesLogic = false;
    if (logicFilters.unregistered && !spot.flags.includes("Unregistered"))
      matchesLogic = false;
    return matchesSearch && matchesLogic;
  });

  // Check if specific hotspot is loading
  const isLogLoading = (id) => loadingStates.log[id] || false;
  const isDispatchLoading = (id) => loadingStates.dispatch[id] || false;

  return (
    <div
      className={`w-full ${bgColorClass} h-[100dvh] flex flex-col font-sans overflow-hidden`}
    >
      <div className="bg-white border-b border-slate-200 px-4 md:px-6 py-2 flex justify-between items-center z-30 shrink-0 h-[60px]">
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColorClass} items-center  gap-x-2`}
        >
          <p>
            <IoColorFillOutline className="cursor-pointer font-semibold" />
          </p>
          <p className="font-semibold">Illegal Filling Detection</p>
        </div>
        <div className="flex gap-3">
          <button className="relative p-2 rounded-full hover:bg-slate-100 text-slate-500">
            <Siren className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden relative">
        <div className="h-[40vh] md:h-full md:flex-1 relative bg-slate-900 group shrink-0">
          <div className="absolute top-0 left-0 w-full p-3 md:p-4 z-20 flex flex-col gap-3 pointer-events-none">
            <div className="flex justify-between items-start pointer-events-auto">
              <div className="bg-slate-900/80 backdrop-blur-md p-2 md:p-3 rounded-lg border border-slate-700 shadow-xl max-w-[200px] md:max-w-none">
                <h2 className="text-white font-bold flex items-center gap-2 text-xs md:text-base">
                  <Crosshair className="w-3 h-3 md:w-4 md:h-4 text-red-500 animate-pulse" />{" "}
                  Live Grid
                </h2>
                <p className="text-[10px] text-slate-400 mt-1 hidden md:block">
                  Satellite Grid: RJ-JP-Sector-14
                </p>
              </div>

              <div className="flex flex-col gap-2 items-end">
                <div className="flex bg-slate-800/90 backdrop-blur rounded-lg p-1 border border-slate-700 shadow-lg">
                  <button
                    onClick={() => toggleLogicFilter("longStop")}
                    className={`p-2 rounded text-[10px] font-medium transition-all ${
                      logicFilters.longStop
                        ? "bg-blue-600 text-white"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => toggleLogicFilter("noRfid")}
                    className={`p-2 rounded text-[10px] font-medium transition-all ${
                      logicFilters.noRfid
                        ? "bg-red-600 text-white"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    <AlertOctagon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => toggleLogicFilter("unregistered")}
                    className={`p-2 rounded text-[10px] font-medium transition-all ${
                      logicFilters.unregistered
                        ? "bg-orange-500 text-white"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    <MapIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 50% 50%, #1e293b 0%, #020617 100%)",
            }}
          >
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  "linear-gradient(#334155 1px, transparent 1px), linear-gradient(90deg, #334155 1px, transparent 1px)",
                backgroundSize: "40px 40px",
              }}
            ></div>
          </div>

          <div className="relative w-full h-full overflow-hidden cursor-crosshair">
            {filteredHotspots.map((spot) => (
              <div
                key={spot.id}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500 cursor-pointer group/marker"
                style={{
                  top: spot.mapPosition.top,
                  left: spot.mapPosition.left,
                }}
                onClick={() => setSelectedSpot(spot)}
              >
                <div
                  className={`absolute -inset-8 rounded-full opacity-30 animate-ping ${
                    spot.intensity === "High" ? "bg-red-500" : "bg-orange-500"
                  }`}
                ></div>
                <div
                  className={`absolute -inset-4 rounded-full opacity-50 blur-md ${
                    spot.intensity === "High" ? "bg-red-600" : "bg-orange-500"
                  }`}
                ></div>
                <div
                  className={`relative w-4 h-4 rounded-full border-2 border-white shadow-[0_0_15px_rgba(255,255,255,0.5)] z-10
                        ${
                          spot.intensity === "High"
                            ? "bg-red-600"
                            : "bg-orange-500"
                        }
                        ${
                          selectedSpot?.id === spot.id
                            ? "scale-150 ring-4 ring-white/20"
                            : "hover:scale-125"
                        }
                      `}
                ></div>

                <div className="hidden md:block absolute top-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] px-2 py-1 rounded border border-slate-700 whitespace-nowrap opacity-0 group-hover/marker:opacity-100 transition-opacity pointer-events-none z-20">
                  {spot.locationName}
                </div>
              </div>
            ))}
          </div>

          <div className="absolute bottom-4 left-4 z-20 pointer-events-none">
            <div className="bg-slate-900/80 backdrop-blur p-2 rounded border border-slate-700 text-[10px] text-slate-400">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></span>{" "}
                Critical
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>{" "}
                Suspicious
              </div>
            </div>
          </div>
        </div>

        <div className="w-full h-full md:w-[420px] bg-slate-50 border-t md:border-t-0 md:border-l border-slate-200 flex flex-col shrink-0 shadow-2xl z-20 overflow-hidden">
          {selectedSpot ? (
            <>
              <div className="p-4 md:p-5 bg-white border-b border-slate-200 shrink-0">
                <div className="flex justify-between items-start mb-2 md:mb-3">
                  <IntensityBadge level={selectedSpot.intensity} />
                  <div className="text-right">
                    <div className="text-[10px] font-mono text-slate-400">
                      CASE ID: {selectedSpot.id}
                    </div>
                    <div className="text-xs font-bold text-slate-700 flex items-center justify-end gap-1">
                      <ShieldAlert className="w-3 h-3 text-slate-400" /> Score:{" "}
                      {selectedSpot.riskScore}/100
                    </div>
                  </div>
                </div>
                <h2 className="text-base md:text-lg font-bold text-slate-900 leading-snug line-clamp-2">
                  {selectedSpot.locationName}
                </h2>

                <div className="flex flex-wrap gap-2 mt-2 md:mt-3">
                  {selectedSpot.flags.map((flag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2 py-1 rounded bg-red-50 text-red-700 text-[10px] font-bold border border-red-100 uppercase tracking-wide"
                    >
                      <AlertTriangle className="w-3 h-3 mr-1" /> {flag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4 md:space-y-6 pb-20 md:pb-5">
                <div className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm relative">
                  <h3 className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-2">
                    <Activity className="w-3 h-3" /> Risk Profile Analysis
                  </h3>
                  <div className="h-[160px] md:h-[180px] w-full flex justify-center items-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart
                        cx="50%"
                        cy="50%"
                        outerRadius="70%"
                        data={selectedSpot.riskProfile}
                      >
                        <PolarGrid stroke="#e2e8f0" />
                        <PolarAngleAxis
                          dataKey="subject"
                          tick={{ fontSize: 10, fill: "#64748b" }}
                        />
                        <PolarRadiusAxis
                          angle={30}
                          domain={[0, 150]}
                          tick={false}
                          axisLine={false}
                        />
                        <Radar
                          name="Risk"
                          dataKey="A"
                          stroke="#ef4444"
                          strokeWidth={2}
                          fill="#ef4444"
                          fillOpacity={0.2}
                        />
                        <Tooltip
                          contentStyle={{
                            borderRadius: "8px",
                            border: "none",
                            fontSize: "12px",
                            boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                          }}
                          itemStyle={{ color: "#ef4444", fontWeight: "bold" }}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm">
                    <p className="text-[10px] text-slate-400 uppercase font-bold">
                      Frequency
                    </p>
                    <p className="text-lg md:text-xl font-bold text-slate-800">
                      {selectedSpot.frequency}{" "}
                      <span className="text-xs font-normal text-slate-400">
                        stops
                      </span>
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm">
                    <p className="text-[10px] text-slate-400 uppercase font-bold">
                      Avg Duration
                    </p>
                    <p className="text-lg md:text-xl font-bold text-slate-800">
                      {selectedSpot.avgStopDuration}
                    </p>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm">
                  <h3 className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-2">
                    <Clock className="w-3 h-3" /> Time Distribution
                  </h3>
                  <div className="h-[100px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={timePatternData}>
                        <Tooltip
                          cursor={{ fill: "#f1f5f9" }}
                          contentStyle={{ fontSize: "10px" }}
                        />
                        <Bar dataKey="stops" radius={[2, 2, 0, 0]}>
                          {timePatternData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.stops > 20 ? "#ef4444" : "#cbd5e1"}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-[10px] text-center text-slate-400 mt-1">
                    Peak: {selectedSpot.peakTime}
                  </p>
                </div>

                <div>
                  <h3 className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-2">
                    <Truck className="w-3 h-3" /> Suspect Tankers
                  </h3>
                  <div className="space-y-2">
                    {selectedSpot.tankersInvolved.map((tanker, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedTanker(tanker)}
                        className="group flex justify-between items-center p-3 rounded-lg bg-white border border-slate-200 hover:border-red-300 hover:shadow-md transition-all cursor-pointer active:scale-95"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 group-hover:bg-red-50 group-hover:text-red-600 transition-colors">
                            <Truck className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-800">
                              {tanker.id}
                            </p>
                            <p className="text-[10px] text-slate-500">
                              {tanker.model}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-red-400" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-4 bg-white border-t border-slate-200 shrink-0 grid grid-cols-2 gap-3 safe-area-bottom">
                <button
                  onClick={(e) => handleLogCase(e, selectedSpot.id)}
                  disabled={
                    selectedSpot.isLogged || 
                    isLogLoading(selectedSpot.id)
                  }
                  className={`flex cursor-pointer items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold transition-all active:scale-95
                        ${
                          selectedSpot.isLogged
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shadow-sm"
                        }
                      `}
                >
                  {isLogLoading(selectedSpot.id) ? (
                    <span className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></span>
                  ) : selectedSpot.isLogged ? (
                    <>
                      <CheckCircle className="w-4 h-4" /> Logged
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" /> Log Case
                    </>
                  )}
                </button>

                <button
                  onClick={(e) => handleDispatch(e, selectedSpot.id)}
                  disabled={
                    selectedSpot.isDispatched || 
                    isDispatchLoading(selectedSpot.id) ||
                    blockRecords.length === 0 || loading
                  }
                  className={`flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold text-white shadow-lg transition-all active:scale-95
                        ${
                          selectedSpot.isDispatched
                            ? "bg-slate-700 cursor-not-allowed"
                            : blockRecords.length === 0
                            ? "bg-slate-400 cursor-not-allowed" // Gray out when no records
                            : "bg-red-600 hover:bg-red-700 hover:shadow-red-500/30"
                        }
                      `}
                >
                  {isDispatchLoading(selectedSpot.id) ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : selectedSpot.isDispatched ? (
                    <>
                      <CheckCircle className="w-4 h-4" /> Team Sent
                    </>
                  ) : blockRecords.length === 0 ? ( 
                    <>
                      <Clock className="w-4 h-4" /> Log First
                    </>
                  ) : (
                    <>
                      <Navigation className="w-4 h-4" /> Dispatch
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 p-8">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <MapIcon className="w-8 h-8 text-slate-300" />
              </div>
              <p className="font-medium text-slate-500">No Location Selected</p>
              <p className="text-sm text-center mt-2">
                Click on a hotspot on the map
              </p>
            </div>
          )}
        </div>
      </div>

      {selectedTanker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="bg-slate-50 p-4 border-b border-slate-100 flex justify-between items-center">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <Truck className="w-4 h-4 text-slate-500" /> Vehicle Details
              </h3>
              <button
                onClick={() => setSelectedTanker(null)}
                className="p-1 hover:bg-slate-200 rounded-full"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-4 md:p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
                  <Truck className="w-8 h-8 text-slate-400" />
                </div>
                <div>
                  <div className="text-xl md:text-2xl font-bold text-slate-900">
                    {selectedTanker.id}
                  </div>
                  <div className="text-sm text-slate-500">
                    {selectedTanker.model}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">
                    Driver
                  </div>
                  <div className="flex items-center gap-2 font-medium text-slate-700 text-sm">
                    <User className="w-3 h-3" /> {selectedTanker.driver}
                  </div>
                </div>
                <div
                  className={`p-3 rounded-lg border ${
                    selectedTanker.history === "Clean Record"
                      ? "bg-green-50 border-green-100 text-green-700"
                      : "bg-red-50 border-red-100 text-red-700"
                  }`}
                >
                  <div className="text-[10px] uppercase font-bold mb-1 opacity-70">
                    History
                  </div>
                  <div className="flex items-center gap-2 font-bold text-sm">
                    {selectedTanker.history}
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <button className="w-full py-3 md:py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors active:scale-95">
                  View Full GPS History
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}